import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement

// Fáze 5 (část 2) — admin správa členů a profilů (medailonků) + upload fotek do bucketu sg-media.
// Profily jsou klíčované na bl_player_id (soupiska z bowlingligy) -> editor jede přes roster.
// Účty (sg_member) jen mění roli/mapování; zakládání účtů se dělá v Supabase (ne v appce).

@Composable
fun MembersAdminSection(token: String) {
    var roster by remember { mutableStateOf<List<RosterPlayerDto>>(emptyList()) }
    var profiles by remember { mutableStateOf<List<SgProfileDto>>(emptyList()) }
    var members by remember { mutableStateOf<List<SgMemberDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }

    LaunchedEffect(reloadKey) {
        try {
            roster = runCatching { Api.fetchTeamProfile().roster }.getOrDefault(emptyList())
            profiles = Api.fetchProfilesAdmin(token)
            members = Api.fetchAllMembers(token)
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    if (loading) {
        Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        return
    }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    val profileById = remember(profiles) { profiles.associateBy { it.blPlayerId } }
    val nameById = remember(roster) { roster.associate { it.playerId to (it.fullName ?: it.name) } }

    ProfilesAdmin(token, roster, profileById, onChanged = { reloadKey++ })
    MembersAdmin(token, members, nameById, onChanged = { reloadKey++ })
}

@Composable
private fun ProfilesAdmin(
    token: String,
    roster: List<RosterPlayerDto>,
    profileById: Map<String, SgProfileDto>,
    onChanged: () -> Unit,
) {
    Div(attrs = { classes("card") }) {
        H3 { Text("Profily hráčů (medailonky)") }
        P(attrs = { classes("muted") }) { Text("Jméno, přezdívka, foto a viditelnost každého hráče na hlavní stránce.") }
        if (roster.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Soupiska z bowlingligy je prázdná.") }
            return@Div
        }
        Div(attrs = { classes("profile-admin-list") }) {
            roster.forEach { player ->
                key(player.playerId) {
                    ProfileEditor(token, player, profileById[player.playerId], onChanged)
                }
            }
        }
    }
}

@Composable
private fun ProfileEditor(token: String, player: RosterPlayerDto, existing: SgProfileDto?, onChanged: () -> Unit) {
    var displayName by remember { mutableStateOf(existing?.displayName ?: player.fullName ?: player.name) }
    var nickname by remember { mutableStateOf(existing?.nickname ?: "") }
    var photoUrl by remember { mutableStateOf(existing?.photoUrl ?: "") }
    var bioHtml by remember { mutableStateOf(existing?.bioHtml ?: "") }
    var isCaptain by remember { mutableStateOf(existing?.isCaptain ?: false) }
    var isActive by remember { mutableStateOf(existing?.isActive ?: true) }
    var sortOrder by remember { mutableStateOf(existing?.sortOrder ?: 0) }
    var busy by remember { mutableStateOf(false) }
    var uploading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("profile-admin-item") }) {
        Div(attrs = { classes("profile-admin-photo") }) {
            if (photoUrl.isNotBlank()) {
                Img(src = photoUrl, alt = displayName, attrs = { classes("profile-thumb") })
            } else {
                Div(attrs = { classes("avatar-placeholder", "profile-thumb") }) { Text(displayName.take(1)) }
            }
            Label(attrs = { classes("small-btn", "upload-btn") }) {
                Text(if (uploading) "Nahrávám…" else "Foto")
                Input(type = InputType.File) {
                    attr("accept", "image/*")
                    style { property("display", "none") }
                    if (uploading) disabled()
                    onChange { ev ->
                        val input = ev.nativeEvent.target as? HTMLInputElement ?: return@onChange
                        val file = input.files?.item(0) ?: return@onChange
                        uploading = true; message = null
                        scope.launch {
                            try {
                                val bytes = readFileBytes(file)
                                val name = mediaFileName("player-${player.playerId}", file)
                                photoUrl = Api.uploadMedia(token, name, file.type.ifBlank { "image/jpeg" }, bytes)
                            } catch (e: Throwable) {
                                message = "Upload selhal: ${e.apiMessage()}"
                            } finally { uploading = false }
                        }
                    }
                }
            }
            if (photoUrl.isNotBlank()) {
                Button(attrs = { classes("small-btn"); onClick { photoUrl = "" } }) { Text("Odebrat foto") }
            }
        }
        Div(attrs = { classes("profile-admin-fields") }) {
            Label { Text("Jméno"); Input(type = InputType.Text) { classes("field"); value(displayName); onInput { displayName = it.value } } }
            Label { Text("Přezdívka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(nickname); onInput { nickname = it.value } } }
            Div(attrs = { classes("editor-label") }) { Text("Text (medailonek)") }
            key(player.playerId) { ContentEditor(existing?.bioHtml ?: "", onHtmlChange = { bioHtml = it }) }
            Div(attrs = { classes("profile-admin-flags") }) {
                Label(attrs = { classes("cms-check") }) { CheckboxInput(checked = isCaptain) { onChange { isCaptain = it.value } }; Text(" Kapitán") }
                Label(attrs = { classes("cms-check") }) { CheckboxInput(checked = isActive) { onChange { isActive = it.value } }; Text(" Aktivní") }
                Label(attrs = { classes("cms-check") }) {
                    Text("Pořadí ")
                    Input(type = InputType.Number) { classes("field", "field-narrow"); value(sortOrder.toString()); onInput { sortOrder = it.value?.toInt() ?: 0 } }
                }
            }
            Div(attrs = { classes("form-actions") }) {
                Button(attrs = {
                    classes("primary-btn")
                    if (busy || uploading) disabled()
                    onClick {
                        if (displayName.isBlank()) { message = "Zadej jméno."; return@onClick }
                        busy = true; message = null
                        scope.launch {
                            try {
                                Api.upsertProfile(token, player.playerId, displayName.trim(), nickname, photoUrl, bioHtml, isCaptain, isActive, sortOrder)
                                message = "Uloženo."
                                onChanged()
                            } catch (e: Throwable) {
                                message = "Uložení selhalo: ${e.apiMessage()}"
                            } finally { busy = false }
                        }
                    }
                }) { Text(if (busy) "Ukládám…" else "Uložit") }
                message?.let { Span(attrs = { classes("form-note") }) { Text(it) } }
            }
        }
    }
}

@Composable
private fun MembersAdmin(token: String, members: List<SgMemberDto>, nameById: Map<String, String>, onChanged: () -> Unit) {
    Div(attrs = { classes("card") }) {
        H3 { Text("Účty členů") }
        P(attrs = { classes("muted") }) { Text("Role viewer pouze čte, player zapisuje a admin spravuje web. Účty se zakládají v Supabase.") }
        if (members.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Žádné účty.") }
            return@Div
        }
        Div(attrs = { classes("member-admin-list") }) {
            members.forEach { m ->
                key(m.userId) { MemberRow(token, m, nameById, onChanged) }
            }
        }
    }
}

@Composable
private fun MemberRow(token: String, member: SgMemberDto, nameById: Map<String, String>, onChanged: () -> Unit) {
    var role by remember { mutableStateOf(member.role) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val playerName = member.blPlayerId?.let { nameById[it] } ?: "bez mapování"

    Div(attrs = { classes("member-admin-item") }) {
        Div(attrs = { classes("member-admin-info") }) {
            Span(attrs = { classes("member-admin-name") }) { Text(playerName) }
            Span(attrs = { classes("muted") }) { Text(member.userId.take(8)) }
        }
        Div(attrs = { classes("member-admin-controls") }) {
            Select(attrs = {
                classes("field")
                onChange { ev -> ev.value?.let { role = it } }
            }) {
                Option("viewer", attrs = { if (role == "viewer") selected() }) { Text("viewer") }
                Option("player", attrs = { if (role == "player") selected() }) { Text("player") }
                Option("admin", attrs = { if (role == "admin") selected() }) { Text("admin") }
            }
            Button(attrs = {
                classes("small-btn")
                if (busy || role == member.role) disabled()
                onClick {
                    busy = true; message = null
                    scope.launch {
                        try {
                            Api.updateMember(token, member.userId, role, member.blPlayerId)
                            message = "Uloženo."
                            onChanged()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "…" else "Uložit roli") }
            message?.let { Span(attrs = { classes("form-note") }) { Text(it) } }
        }
    }
}
